import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Zap, RefreshCw, ShoppingBag, Calendar, Check, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminDispoWeekend() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/disponibilites/weekend`, { headers: H });
      setData(await res.json());
      setSelected([]);
    } catch { toast.error('Erreur'); }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  function toggleSelect(id) {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function copyPromo() {
    const prods = (data?.produits_libres || []).filter(p => selected.includes(p.id));
    const text = prods.map(p => `- ${p.nom} : ${p.tarif_public}u20ac/jour`).join('\n');
    const msg = `🎉 OFFRES WEEKEND Show u00c9vu00e9nement\n${data?.weekend?.samedi} & ${data?.weekend?.dimanche}\n\n${text}\n\nRu00e9servez duu00e8s maintenant → configurateur-devis.preview.emergentagent.com`;
    navigator.clipboard?.writeText(msg).then(() => toast.success('Texte copiu00e9 dans le presse-papier !'))
      .catch(() => toast.error('Impossible de copier'));
  }

  const produits = data?.produits_libres || [];

  return (
    <div data-testid="admin-page">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Paramètres</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>Offres weekend</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Configuration des offres weekend</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }} data-testid="admin-dispo-weekend">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="adm-title"  style={{ color: 'var(--admin-green)' }}>Offres weekend</h1>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Produits libres le prochain weekend</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} variant="outline" size="sm" style={{ borderColor: 'var(--admin-border)', color: 'var(--admin-text)' }}>
            <RefreshCw size={14} className="mr-1" /> Actualiser
          </Button>
          {selected.length > 0 && (
            <Button onClick={copyPromo} size="sm" style={{ background: 'rgba(31,157,122,0.2)', color: 'var(--admin-green)', border: '1px solid rgba(31,157,122,0.3)' }}>
              <ShoppingBag size={14} className="mr-1" /> Copier offre ({selected.length})
            </Button>
          )}
        </div>
      </div>

      {/* Info weekend */}
      {data?.weekend && (
        <div className="flex items-center gap-3 p-4 rounded-2xl" style={{ background: 'rgba(31,157,122,0.08)', border: '1px solid rgba(31,157,122,0.2)' }}>
          <Calendar size={18} style={{ color: 'var(--admin-green)' }} />
          <div>
            <p className="font-semibold text-sm" style={{ color: 'var(--admin-text)' }}>
              Prochain weekend : {data.weekend.samedi} (Samedi) & {data.weekend.dimanche} (Dimanche)
            </p>
            <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
              {data.total_libres} produit{data.total_libres > 1 ? 's' : ''} disponible{data.total_libres > 1 ? 's' : ''} · {data.total_reserves} ru00e9servu00e9{data.total_reserves > 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}

      {/* Instruction */}
      <div className="p-3 rounded-xl" style={{ background: 'var(--admin-teal-dim)', border: '1px solid rgba(93,188,191,0.15)' }}>
        <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>
          Su00e9lectionnez les produits libres u00e0 mettre en avant puis copiez le texte promu0 pour le diffuser sur vos ru00e9seaux ou par email.
        </p>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-32 rounded-xl" style={{ background: 'var(--admin-surface)' }} />)}</div>
      ) : produits.length === 0 ? (
        <div className="text-center py-16" style={{ color: 'var(--admin-faint)' }}>
          <Package size={40} className="mx-auto mb-3 opacity-20" />
          <p>Tous les produits sont ru00e9servu00e9s ce weekend !</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {produits.map(p => {
            const sel = selected.includes(p.id);
            return (
              <button key={p.id}
                onClick={() => toggleSelect(p.id)}
                className="text-left p-4 rounded-xl transition-all"
                style={{
                  background: sel ? 'rgba(31,157,122,0.12)' : '#161616',
                  border: sel ? '1px solid rgba(31,157,122,0.4)' : '1px solid rgba(255,255,255,0.06)',
                }}>
                <div className="flex items-start gap-3">
                  {p.images?.[0] && (
                    <img src={p.images[0]} alt={p.nom} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-sm truncate" style={{ color: 'var(--admin-text)' }}>{p.nom}</p>
                      {sel && <Check size={14} style={{ color: 'var(--admin-green)', flexShrink: 0 }} />}
                    </div>
                    <p className="text-lg font-bold" style={{ color: 'var(--admin-gold)' }}>{p.tarif_public}€<span className="text-xs font-normal" style={{ color: 'var(--admin-faint)' }}>/jour</span></p>
                    {p.featured && <span className="text-xs" style={{ color: 'var(--admin-amber)' }}>★ Bestseller</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}
